/**
 * "Quem mais vê esta OS" — a OS dada a alguém de fora da loja dela.
 *
 * O caso que o criou (16/10/2026): o instalador de Búzios mandado a Trancoso
 * vê aquelas OS, e só elas. No servidor é uma atribuição de âmbito OS
 * (migration 126) — a mesma regra das lojas, num âmbito menor; não há
 * "override" em lugar nenhum. Cada linha diz quem deu, por quê e até quando, e
 * tirar não apaga: marca.
 *
 * Quem vê o bloco: `ordens-servico.acessos.ver`. Quem dá e tira:
 * `ordens-servico.acessos.gerir` — as duas checadas NA loja desta OS.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { CustomCampoData } from '@/components/CustomCamposData';
import { api, ApiError } from '@/lib/api';
import { formatarData } from '@/lib/data';

type AcessoOS = {
  id: number;
  usuario_id: number;
  usuario_nome: string;
  perfil_nome: string | null;
  fim: string | null;
  motivo: string | null;
  criacao: string | null;
  quem: string | null;
};

type Pessoa = { id: number; nome: string };

export function CustomPainelAcessosOS({
  osId,
  pessoas,
  podeGerir,
}: {
  osId: number;
  /** Quem pode receber — a mesma lista do "Responsável". */
  pessoas: Pessoa[];
  podeGerir: boolean;
}) {
  const [lista, setLista] = useState<AcessoOS[]>([]);
  const [pessoa, setPessoa] = useState('');
  const [motivo, setMotivo] = useState('');
  const [fim, setFim] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    api
      .get<AcessoOS[]>(`/ordens-servico/${osId}/acessos`)
      .then(setLista)
      .catch(() => setLista([]));
  }, [osId]);

  async function dar() {
    if (!pessoa || !motivo.trim()) {
      toast.error('Escolha a pessoa e diga por que ela recebe esta OS.');
      return;
    }
    setEnviando(true);
    try {
      const nova = await api.post<AcessoOS[]>(`/ordens-servico/${osId}/acessos`, {
        usuario_id: Number(pessoa),
        motivo: motivo.trim(),
        fim: fim || null,
      });
      setLista(nova);
      setPessoa('');
      setMotivo('');
      setFim('');
      toast.success('A pessoa passa a ver esta OS.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível dar o acesso.');
    } finally {
      setEnviando(false);
    }
  }

  async function tirar(a: AcessoOS) {
    try {
      await api.delete(`/ordens-servico/${osId}/acessos/${a.id}`);
      setLista((l) => l.filter((x) => x.id !== a.id));
      toast.success(`${a.usuario_nome} deixa de ver esta OS.`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível tirar o acesso.');
    }
  }

  return (
    <div className="card adminuiux-card mb-3">
      <div className="card-body">
        <h6 className="mb-1">
          <i className="bi bi-person-plus text-theme-1 me-2"></i>
          Quem mais vê esta OS
        </h6>
        <p className="small text-secondary mb-3">
          Pessoas de fora da loja desta OS que receberam acesso só a ela — o
          instalador mandado de outra loja, por exemplo. Quem é da loja já vê
          pelo próprio acesso.
        </p>

        {lista.length === 0 ? (
          <p className="small text-secondary mb-3">Ninguém de fora recebeu esta OS.</p>
        ) : (
          <ul className="list-group list-group-flush mb-3">
            {lista.map((a) => (
              <li key={a.id} className="list-group-item px-0 d-flex align-items-start gap-2">
                <div className="flex-grow-1">
                  <div className="fw-medium">
                    {a.usuario_nome}
                    {a.perfil_nome && (
                      <span className="text-secondary fw-normal"> · {a.perfil_nome}</span>
                    )}
                  </div>
                  <div className="small text-secondary">
                    {a.motivo}
                    {' · '}
                    {a.fim ? `até ${formatarData(a.fim)}` : 'sem prazo'}
                    {a.quem && ` · deu: ${a.quem}`}
                    {a.criacao && ` em ${formatarData(a.criacao)}`}
                  </div>
                </div>
                {podeGerir && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => tirar(a)}
                  >
                    Tirar
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {podeGerir && (
          <div className="row g-3">
            <div className="col-12 col-md-4">
              <div className="form-floating">
                <select
                  id="acesso-os-pessoa"
                  className="form-select"
                  value={pessoa}
                  onChange={(e) => setPessoa(e.target.value)}
                >
                  <option value="">Selecione</option>
                  {pessoas.map((p) => (
                    <option key={p.id} value={String(p.id)}>
                      {p.nome}
                    </option>
                  ))}
                </select>
                <label htmlFor="acesso-os-pessoa">Pessoa</label>
              </div>
            </div>
            <div className="col-12 col-md-5">
              <div className="form-floating">
                <input
                  id="acesso-os-motivo"
                  className="form-control"
                  placeholder="Por quê"
                  maxLength={255}
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                />
                <label htmlFor="acesso-os-motivo">Por quê</label>
              </div>
            </div>
            <div className="col-12 col-md-3">
              <CustomCampoData
                id="acesso-os-fim"
                label="Até (opcional)"
                valor={fim}
                onChange={(iso) => setFim(iso)}
              />
            </div>
            <div className="col-12 text-end">
              <button
                type="button"
                className="btn btn-theme"
                disabled={enviando}
                onClick={dar}
              >
                <i className="bi bi-person-check me-1"></i> Dar acesso a esta OS
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
